import { decryptPrivateKey } from "./decryptPrivateKey";
import { getKeys } from "./getKeys";
import { getActiveProfilePrivateKeys } from "./activeProfileKeys";
import * as openpgp from "openpgp";
import { readFileFromBase64 } from "./readFileFromBase64";
import { logError } from "./loggerToBE";
import { IPackage } from "dto/IPackage";
import { IKey } from "dto/IKey";

interface IDecryptedKeys {
  id: string;
  key: openpgp.key.KeyResult;
}

const DECRYPTED_KEYS: IDecryptedKeys[] = [];

/**
 * Usuwa z cache'u odszyfrowane klucze obiektów o podanych Id (po przeszyfrowaniu —
 * cache trzymałby wersje sprzed rotacji). Bez argumentu czyści cały cache.
 */
export const invalidateDecryptedKeys = (ids?: string[]) => {
  if (ids === undefined) {
    DECRYPTED_KEYS.length = 0;
    return;
  }

  if (!ids.length) {
    return;
  }

  for (const id of ids) {
    const index = DECRYPTED_KEYS.findIndex((k) => k?.id === id);
    if (index >= 0) DECRYPTED_KEYS.splice(index, 1);
  }
};

const decodeNotArmored = async (
  pack: string | IPackage,
  key: openpgp.key.KeyResult,
) => {
  const tresc = await readFileFromBase64(pack as string, "application/other");
  console.log("Read ok");
  const uint8 = new Uint8Array(tresc);

  const xpack = await openpgp.message.read(uint8);
  console.log("Xpack", xpack);
  const decryptedPackage = await openpgp.decrypt({
    message: xpack,
    // Cała lista kluczy-kandydatów (obiekt / profil + fallback) — openpgp wybiera po ID.
    privateKeys: key.keys,
    // privateKeys: [],
    format: "binary",
  });

  return new TextDecoder().decode(decryptedPackage.data);
};

const decodeArmored = async (
  pack: string | IPackage,
  key: openpgp.key.KeyResult,
) => {
  const xpack = await openpgp.message.readArmored(pack);
  const decryptedPackage = await openpgp.decrypt({
    message: xpack,
    privateKeys: key.keys,
    format: "binary",
  });

  return new TextDecoder().decode(decryptedPackage.data);
};

const decodeString = async (
  pack: string | IPackage,
  key: openpgp.key.KeyResult,
) => {
  const tresc = await readFileFromBase64(pack as string, "application/other");
  const xpack = await openpgp.message.read(new Uint8Array(tresc));
  const decryptedPackage = await openpgp.decrypt({
    message: xpack,
    privateKeys: key.keys,
    // privateKeys: [],
    format: "binary",
  });

  return new TextDecoder().decode(decryptedPackage.data);
};

const decodeKey = async (
  userId: string,
  keys?: IKey,
  parentKeys?: IKey | undefined | null,
  webWorkerMyKeys?: any,
  contactMessageChatId?: string,
) => {
  console.log("DecodeKey, chatId", contactMessageChatId);
  const myKeys = webWorkerMyKeys
    ? webWorkerMyKeys
    : await getKeys(userId, contactMessageChatId);
  let privateKey: null | openpgp.DecryptResult = null;

  if (parentKeys) {
    const decryptParentKey = await decryptPrivateKey(myKeys!, parentKeys);

    const text = await openpgp.key.read(decryptParentKey.data);

    if (!keys?.PrivateKey) {
      throw new Error("no key");
    }

    const PK = await readFileFromBase64(keys.PrivateKey, "application/other");

    const msg = await openpgp.message.read(new Uint8Array(PK));

    const options: openpgp.DecryptOptions = {
      message: msg,
      privateKeys: text.keys[0],
      format: "binary",
    };

    privateKey = await openpgp.decrypt(options);
  } else {
    if (keys?.PrivateKey) {
      privateKey = await decryptPrivateKey(myKeys!, keys);
    }
  }

  let key: null | openpgp.key.KeyResult = null;

  if (privateKey) {
    key = await openpgp.key.read(privateKey.data);
  } else {
    // Brak klucza obiektu — pakiet jest zaszyfrowany bezpośrednio kluczem najwyższego rzędu,
    // czyli od czasu profili kluczem AKTYWNEGO PROFILU, nie intermediate. Podajemy klucze
    // profilu (pierwotny + dodatkowe ze scaleń) i klucz intermediate jako fallback — openpgp
    // dobierze właściwy po ID odbiorcy z szyfrogramu.
    const intermediate = await openpgp.key.readArmored(
      atob(myKeys!.intermidatePrivateKey!.data),
    );
    key = {
      keys: [...getActiveProfilePrivateKeys(), ...intermediate.keys],
      err: null,
    } as openpgp.key.KeyResult;
  }

  if (keys?.EmptyPassphrase) {
    for (const k of key.keys) {
      try {
        k.decrypt("");
      } catch {
        // już odblokowany / bez hasła — ignorujemy
      }
    }
  }

  if (keys && !DECRYPTED_KEYS.find((k) => k.id === keys?.Id)) {
    DECRYPTED_KEYS.push({ id: keys.Id, key: key });
  }

  return key;
};

export const decryptPackage = async (
  userId: string,
  pack: IPackage | string,
  keys?: IKey,
  parentKeys?: IKey,
  webWorkerMyKeys?: any,
  contactMessageChatId?: string,
) => {
  let key: null | openpgp.key.KeyResult = null;
  if (DECRYPTED_KEYS.find((k) => k?.id === keys?.Id)) {
    key = DECRYPTED_KEYS.find((k) => k?.id === keys?.Id)?.key!;
  } else {
    key = await decodeKey(
      userId,
      keys,
      parentKeys,
      webWorkerMyKeys,
      contactMessageChatId,
    );
  }

  if (!key) {
    throw new Error("Key not found");
  }

  console.log("Decrypting package", key, contactMessageChatId);

  try {
    return await decodeNotArmored(pack, key);
  } catch (e) {
    try {
      return await decodeArmored(pack, key);
    } catch (e) {
      logError(`[DecryptPackage] ${e}`);
      return await decodeString(pack, key);
    }
  }
};
